/**
 * Downsize sale economics and purchase-price candidates for Support at Home suggestion engine.
 * Mirrors Aged Care Strategy Analysis Setup (optional new home value + selling costs).
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';

const DEFAULT_SELLING_COST_RATE = 0.025;

/** Property-strategy settings from the Support at Home suggestion-engine setup UI. */
export interface DownsizePropertySettings {
    downsize?: boolean;
    downsizeSellingCosts?: number;
    sellingCosts?: number;
    downsizeNewHomeValue?: number;
    newHomeValue?: number;
    increaseHomeEquity?: boolean;
    renovate?: boolean;
    upgrade?: boolean;
    reverseMortgage?: boolean;
    heas?: boolean;
    [key: string]: unknown;
}

export interface DownsizeSaleEconomics {
    salePrice: number;
    sellingCosts: number;
    usesCustomSellingCosts: boolean;
}

/**
 * Resolve sale price and selling costs for a downsize scenario.
 */
export function resolveDownsizeSaleEconomics(
    currentHomeValue: number,
    propertySettings: DownsizePropertySettings = {},
): DownsizeSaleEconomics {
    const homeValue = Math.max(0, currentHomeValue);
    const userSellingCosts = propertySettings.downsizeSellingCosts ?? propertySettings.sellingCosts;

    if (typeof userSellingCosts === 'number' && Number.isFinite(userSellingCosts) && userSellingCosts > 0) {
        return {
            salePrice: homeValue,
            sellingCosts: userSellingCosts,
            usesCustomSellingCosts: true,
        };
    }

    return {
        salePrice: homeValue * (1 - DEFAULT_SELLING_COST_RATE),
        sellingCosts: homeValue * DEFAULT_SELLING_COST_RATE,
        usesCustomSellingCosts: false,
    };
}

/**
 * Purchase prices to model. User-entered target, or 50%–80% of current home when blank.
 */
export function getDownsizeNewHomePurchasePrices(
    currentHomeValue: number,
    propertySettings: DownsizePropertySettings = {},
): number[] {
    const homeValue = Math.max(0, currentHomeValue);
    if (homeValue <= 0) return [];

    const userPrice = propertySettings.downsizeNewHomeValue ?? propertySettings.newHomeValue;

    if (typeof userPrice === 'number' && Number.isFinite(userPrice) && userPrice > 0) {
        const capped = Math.min(userPrice, homeValue - 1);
        return capped > 0 ? [capped] : [];
    }

    return [0.5, 0.6, 0.7, 0.8]
        .map((ratio) => homeValue * ratio)
        .filter((price) => price > 0 && price < homeValue);
}

export function buildDownsizeAssumptionLines(
    factFind: DraftFactFind | null | undefined,
    propertySettings: DownsizePropertySettings = {},
): string[] {
    if (!propertySettings?.downsize) return [];

    const homeValue = Math.max(0, factFind?.home?.value || 0);
    const lines: string[] = [];
    const userPrice = propertySettings.downsizeNewHomeValue ?? propertySettings.newHomeValue;
    const { sellingCosts, usesCustomSellingCosts } = resolveDownsizeSaleEconomics(homeValue, propertySettings);

    if (typeof userPrice === 'number' && Number.isFinite(userPrice) && userPrice > 0) {
        lines.push(
            `Downsize target purchase price: $${Math.round(userPrice).toLocaleString()} (entered in Strategy Analysis Setup).`
        );
    } else {
        lines.push(
            'Downsize purchase prices default to 50%, 60%, 70%, and 80% of current home value when no target price is entered.'
        );
    }

    if (usesCustomSellingCosts) {
        lines.push(
            `Downsize selling costs: $${Math.round(sellingCosts).toLocaleString()} (entered in Strategy Analysis Setup).`
        );
    } else {
        lines.push(
            `Downsize selling costs default to ${(DEFAULT_SELLING_COST_RATE * 100).toFixed(1)}% of current home value unless estimated selling costs are entered.`
        );
    }

    return lines;
}
